import { useState } from "react"
import { Card, CardContent } from "../components/ui/card"
import { Button } from "../components/ui/button"

interface Game {
  id: string
  startTime: string
  startTimeEpoch: number
  gameState: string
  teams: {
    home: TeamScore
    away: TeamScore
  }
  currentPeriod?: string
  clock?: string
}

interface TeamScore {
  names: {
    char6: string
    short: string
    full: string
  }
  seed?: number
  score?: string
  winner?: boolean
}

type DateOption = 'yesterday' | 'today' | 'tomorrow'

function getDateString(offset: number): string {
  const date = new Date()
  date.setDate(date.getDate() + offset)
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}/${month}/${day}`
}

function getDateLabel(offset: number): string {
  const date = new Date()
  date.setDate(date.getDate() + offset)
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
}

export default function LiveScores() {
  const [games, setGames] = useState<Game[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null)
  const [selectedDate, setSelectedDate] = useState<DateOption>('today')
  const [hasFetched, setHasFetched] = useState(false)

  const dateOptions: { key: DateOption; label: string; offset: number }[] = [
    { key: 'yesterday', label: getDateLabel(-1), offset: -1 },
    { key: 'today', label: 'Today', offset: 0 },
    { key: 'tomorrow', label: getDateLabel(1), offset: 1 },
  ]

  const fetchScores = async () => {
    const offset = dateOptions.find(d => d.key === selectedDate)?.offset || 0
    setLoading(true)
    setError('')
    setHasFetched(true)
    
    try {
      // Build URL with date parameter
      const dateStr = getDateString(offset)
      const baseUrl = offset === 0 
        ? 'https://ncaa-api.henrygd.me/scoreboard/basketball-men/d1'
        : `https://ncaa-api.henrygd.me/scoreboard/basketball-men/d1/${dateStr}`
      
      // Use corsproxy.io - a reliable CORS proxy
      const url = `https://corsproxy.io/?${encodeURIComponent(baseUrl)}`
      
      const response = await fetch(url)
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }
      
      const data = await response.json()
      
      // Debug: log raw response to see structure
      console.log('NCAA API raw response:', data)
      
      // Extract games - handle different possible API structures
      const rawGames = data?.games || []
      console.log('Raw games array:', rawGames.length, 'games found')
      
      if (rawGames.length > 0) {
        console.log('First game full structure:', JSON.stringify(rawGames[0], null, 2))
        const firstGame = rawGames[0].game || rawGames[0]
        console.log('First game keys:', Object.keys(firstGame))
        if (firstGame.teams) {
          console.log('Teams structure:', firstGame.teams)
        } else if (firstGame.home) {
          console.log('Found home/away at top level:', firstGame.home, firstGame.away)
        }
      }
      
      // Parse games - try multiple possible structures
      const gamesData = rawGames
        .map((item: any, index: number) => {
          // The API nests game data in 'game' property
          const gameData = item.game || item
          
          // Try multiple possible team structures:
          // 1. gameData.teams.home/away
          // 2. gameData.home/away directly
          // 3. item.home/away
          let homeTeam = gameData.teams?.home || gameData.home || item.home
          let awayTeam = gameData.teams?.away || gameData.away || item.away
          
          if (!homeTeam || !awayTeam) {
            if (index === 0) {
              console.log('Game 0 detailed debug - gameData:', gameData)
            }
            return null
          }
          
          return {
            id: gameData.gameID || gameData.id || `game-${index}`,
            startTime: gameData.startTime || gameData.startDate || '',
            startTimeEpoch: gameData.startTimeEpoch || 0,
            gameState: gameData.gameState || gameData.status || 'pre',
            currentPeriod: gameData.currentPeriod || gameData.period,
            clock: gameData.contestClock || gameData.clock,
            teams: {
              home: {
                names: homeTeam.names || { 
                  char6: homeTeam.shortName || homeTeam.name || homeTeam.teamName || 'TBD', 
                  short: homeTeam.shortName || homeTeam.name || homeTeam.teamName || 'TBD', 
                  full: homeTeam.name || homeTeam.teamName || 'TBD' 
                },
                seed: homeTeam.seed,
                score: homeTeam.score,
                winner: homeTeam.winner
              },
              away: {
                names: awayTeam.names || { 
                  char6: awayTeam.shortName || awayTeam.name || awayTeam.teamName || 'TBD', 
                  short: awayTeam.shortName || awayTeam.name || awayTeam.teamName || 'TBD', 
                  full: awayTeam.name || awayTeam.teamName || 'TBD' 
                },
                seed: awayTeam.seed,
                score: awayTeam.score,
                winner: awayTeam.winner
              }
            }
          }
        })
        .filter((game: any) => game !== null)
      
      console.log('Parsed games:', gamesData.length, gamesData)
      setGames(gamesData)
      setLastUpdated(new Date())
      setError('')
    } catch (err) {
      console.error('Error fetching scores:', err)
      setError('Unable to load scores. The NCAA API may be temporarily unavailable.')
      setGames([])
    } finally {
      setLoading(false)
    }
  }

  const getGameStatus = (game: Game) => {
    if (game.gameState === 'final') return 'Final'
    if (game.gameState === 'live') {
      return game.clock ? `${game.currentPeriod} - ${game.clock}` : 'Live'
    }
    if (game.gameState === 'pre') {
      const date = new Date(game.startTimeEpoch * 1000)
      return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
    }
    return game.gameState
  }

  const handleDateChange = (key: DateOption) => {
    setSelectedDate(key)
    setHasFetched(false)
    setGames([])
    setError('')
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-[#1e3a5f]">Scores</h1>
          <p className="text-gray-500 text-sm">
            NCAA Men's Basketball
            {lastUpdated && (
              <span className="ml-2">
                • Updated {lastUpdated.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
              </span>
            )}
          </p>
        </div>
      </div>

      {/* Date Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
        {dateOptions.map(option => (
          <button
            key={option.key}
            onClick={() => handleDateChange(option.key)}
            className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
              selectedDate === option.key
                ? 'bg-white text-[#1e3a5f] shadow-sm'
                : 'text-gray-600 hover:text-[#1e3a5f]'
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>

      {/* Initial State - Show fetch button */}
      {!hasFetched && !loading && (
        <Card className="bg-white border-0 shadow-sm">
          <CardContent className="py-12 text-center">
            <div className="text-gray-400 mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="mx-auto mb-3">
                <circle cx="12" cy="12" r="10"/>
                <polyline points="12 6 12 12 16 14"/>
              </svg>
              Live scores from NCAA
            </div>
            <Button
              onClick={fetchScores}
              className="bg-[#1e3a5f] hover:bg-[#002a6b] text-white"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="mr-2">
                <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8"/>
                <path d="M21 3v5h-5"/>
              </svg>
              Load Scores
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Loading State */}
      {loading && (
        <Card className="bg-white border-0 shadow-sm">
          <CardContent className="py-12 text-center">
            <div className="flex items-center justify-center gap-2 text-gray-500">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="animate-spin">
                <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8"/>
                <path d="M21 3v5h-5"/>
              </svg>
              Loading scores...
            </div>
          </CardContent>
        </Card>
      )}

      {/* Error State */}
      {!loading && error && (
        <Card className="bg-red-50 border border-red-200">
          <CardContent className="py-6 text-center">
            <div className="text-red-700 mb-4">{error}</div>
            <Button
              onClick={fetchScores}
              variant="outline"
              className="border-red-300 text-red-700 hover:bg-red-100"
            >
              Try Again
            </Button>
          </CardContent>
        </Card>
      )}

      {/* No Games */}
      {!loading && !error && hasFetched && games.length === 0 && (
        <Card className="bg-white border-0 shadow-sm">
          <CardContent className="py-12 text-center">
            <div className="text-gray-400 mb-2">
              No games {selectedDate === 'yesterday' ? 'yesterday' : selectedDate === 'tomorrow' ? 'tomorrow' : 'today'}
            </div>
            <p className="text-gray-500 text-sm mb-4">
              {selectedDate === 'tomorrow' ? 'Schedule may not be available yet' : 'Try a different date'}
            </p>
            <Button
              onClick={fetchScores}
              variant="outline"
              size="sm"
              className="border-gray-300"
            >
              Refresh
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Games List */}
      {!loading && !error && games.length > 0 && (
        <>
          <div className="flex justify-end">
            <Button
              onClick={fetchScores}
              variant="outline"
              size="sm"
              className="border-gray-300"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="mr-1.5">
                <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8"/>
                <path d="M21 3v5h-5"/>
              </svg>
              Refresh
            </Button>
          </div>
          
          <div className="space-y-3">
            {games.map((game, index) => (
              <GameCard key={game.id || `game-${index}`} game={game} getGameStatus={getGameStatus} />
            ))}
          </div>
          
          <div className="text-center text-gray-400 text-xs py-2">
            {games.length} game{games.length !== 1 ? 's' : ''} • Tap Refresh to update
          </div>
        </>
      )}
    </div>
  )
}

function GameCard({ game, getGameStatus }: { game: Game; getGameStatus: (game: Game) => string }) {
  // Safety check
  if (!game.teams?.home || !game.teams?.away) {
    return null
  }

  const isLive = game.gameState === 'live'
  const isFinal = game.gameState === 'final'
  const status = getGameStatus(game)

  return (
    <Card className={`bg-white border-0 shadow-sm overflow-hidden ${isLive ? 'ring-2 ring-[#F15A22]' : ''}`}>
      <CardContent className="p-0">
        {/* Status Bar */}
        <div className={`px-3 py-1.5 text-xs font-medium ${
          isLive ? 'bg-[#F15A22] text-white' :
          isFinal ? 'bg-gray-100 text-gray-600' :
          'bg-[#1e3a5f] text-white'
        }`}>
          {isLive && (
            <span className="inline-block w-2 h-2 rounded-full bg-white animate-pulse mr-2" />
          )}
          {status}
        </div>

        {/* Teams */}
        <div className="p-3 space-y-2">
          {/* Away Team */}
          <TeamRow 
            team={game.teams.away} 
            isWinner={isFinal && game.teams.away.winner}
          />
          
          {/* Home Team */}
          <TeamRow 
            team={game.teams.home} 
            isWinner={isFinal && game.teams.home.winner}
          />
        </div>
      </CardContent>
    </Card>
  )
}

function TeamRow({ team, isWinner }: { team: TeamScore; isWinner?: boolean }) {
  if (!team || !team.names) {
    return null
  }

  return (
    <div className={`flex items-center gap-3 ${isWinner ? 'font-bold' : ''}`}>
      {/* Seed */}
      {team.seed && (
        <span className="w-5 h-5 rounded bg-gray-100 text-gray-600 text-xs font-medium flex items-center justify-center">
          {team.seed}
        </span>
      )}
      
      {/* Team Name */}
      <span className={`flex-1 truncate ${isWinner ? 'text-[#1e3a5f]' : 'text-gray-700'}`}>
        {team.names?.short || team.names?.char6 || 'TBD'}
      </span>
      
      {/* Score */}
      <span className={`text-lg font-bold tabular-nums ${
        isWinner ? 'text-[#1e3a5f]' : 'text-gray-500'
      }`}>
        {team.score || '-'}
      </span>
    </div>
  )
}
